import {
  type ClickToTextForm,
  type ClickToTextFormComposed,
  clickToTextPositionVariant,
} from '@/services/domain/services/forms/clickToText/types'
import type FormClickToTextDetails from '@/data/models/domain/services/forms/FormClickToTextDetails'
import type { FormCastService, FormAdditionalSettingsComposed } from '@/services/domain/services/forms/types'
import type { ModelRaw } from '@/types'

export class ClickToTextCastService
  implements FormCastService<FormClickToTextDetails, ClickToTextForm, ClickToTextFormComposed>
{
  public castFormToComposedForm(form: ClickToTextForm): ClickToTextFormComposed {
    return this.castFormToComposedFormSettings(form)
  }

  public castEntityToForm(form: ModelRaw<FormClickToTextDetails>): ClickToTextForm {
    const { id, phone, phoneType, settings } = form

    return {
      id,
      defaultCountryId: form.settings.defaultCountryId,

      // settings
      countries: form.allowedCountryIds,
      contactList: String(form.widgetGroupId),

      // texts
      header: settings.title,
      phoneNumberVisible: !settings.hidePhoneMinimized,
      phoneNumber: phone,
      phoneType,
      description: settings.description,
      submitText: settings.labelButton,
      completedTitle: settings.successMessage,
      completedDescription: settings.additionalText,

      // fields
      nameLabel: settings.labelName,
      nameVisible: !settings.hideName,
      phoneNumberLabel: settings.labelPhone,
      messageLabel: settings.labelMessage,

      // colors
      headerColor: `#${settings.titleColour}`,
      phoneNumberColor: `#${settings.descriptionColour}`,
      submitColor: `#${settings.buttonColour}`,

      // position
      position: clickToTextPositionVariant[settings.position],
    }
  }

  protected castFormToComposedFormSettings(form: ClickToTextForm): ClickToTextFormComposed {
    return {
      // settings
      allowedCountryIds: form.countries,
      defaultCountryId: form.countries[0] || '',
      widgetGroupId: Number(form.contactList),

      // texts
      title: form.header,
      hidePhoneMinimized: !form.phoneNumberVisible,
      phone: form.phoneNumber,
      phoneType: form.phoneType,
      description: form.description,
      labelButton: form.submitText,
      successMessage: form.completedTitle,
      additionalText: form.completedDescription,

      // fields
      labelName: form.nameLabel,
      hideName: !form.nameVisible,
      labelPhone: form.phoneNumberLabel,
      labelMessage: form.messageLabel,

      // colors
      titleColour: form.headerColor.slice(1),
      descriptionColour: form.phoneNumberColor.slice(1),
      buttonColour: form.submitColor.slice(1),

      // position
      position: clickToTextPositionVariant.indexOf(form.position),
    }
  }

  public castFormToPartialEntity(form: ClickToTextForm): Omit<ModelRaw<FormClickToTextDetails>, 'uniqueCode'> {
    return {
      id: form.id,
      phone: form.phoneNumber,
      phoneType: form.phoneType,
      ...this.castFormToComposedFormAdditionalSettings(form),
      settings: this.castFormToComposedFormSettings(form),
    }
  }

  protected castFormToComposedFormAdditionalSettings(form: ClickToTextForm): FormAdditionalSettingsComposed {
    return {
      // settings
      allowedCountryIds: form.countries,
      defaultCountryId: form.countries[0] || '',
      widgetGroupId: Number(form.contactList),
    }
  }
}

export default new ClickToTextCastService()
